import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';

// assets
import logoDark from "../../assest/logo.png";
import login from "../../assest/bg.png";
import FullPageLoader from '../../components/FullPageLoader';

// ==============================|| SIGN IN 1 ||============================== //

const SignIn = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    // App BG: #FFFFFF
    <div className="bg-app-bg p-10 h-screen flex items-center justify-center">

      {/* Primary Surface: #FFF4F8 */}
      <div className="bg-app-primary p-6 md:p-10 rounded-3xl shadow-theme-xl w-full h-full flex flex-col md:flex-row">

        {/* Left Side - Login Form */}
        <div className="md:w-1/2">

          {/* Logo */}
          <div className="flex items-center gap-2 mb-10">
            <img src={logoDark} alt="Logo" className="w-28 h-auto" />
          </div>

          <div className='login mx-auto mb-7'>

            {/* Form Card — clean white surface with soft border */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
              <h4 className="text-2xl font-bold text-app-text pb-1">
                Login
              </h4>

              {/* Subtitle: #666666 */}
              <p className="text-app-subtitle text-theme-sm mb-6">
                Welcome back! Sign in to your account.
              </p>

              <Formik
                initialValues={{ email: '', pass: '', submit: null }}
                validationSchema={Yup.object().shape({
                  email: Yup.string().email('Must be a valid email').required('Email is required'),
                  pass: Yup.string().required('Password is required')
                })}
                onSubmit={async (values, { setErrors, setSubmitting }) => {
                  setLoading(true);
                  try {
                    await axios.post(
                      'https://api.tbh.social/admin/login',
                      { email: values.email, password: values.pass },
                      { withCredentials: true }
                    );
                    navigate('/');
                  } catch (error) {
                    setErrors({ submit: 'Invalid email or password' });
                  }
                  setLoading(false);
                  setSubmitting(false);
                }}
              >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                  <form noValidate onSubmit={handleSubmit}>

                    {/* ── Email Field ── */}
                    <div className="mb-4">
                      {/* Label — app-subtitle */}
                      <label className="block font-semibold mb-1 text-left text-app-subtitle text-theme-sm">
                        Enter Email:
                      </label>
                      <input
                        /* bg: white | border: gray-300 | text: app-text | focus-ring: app-accent */
                        className="
                          w-full px-4 py-2 rounded-lg outline-none
                          bg-white border border-gray-300
                          text-app-text placeholder-gray-400
                          focus:ring-2 focus:ring-app-accent focus:border-app-accent
                          transition-colors
                        "
                        name="email"
                        placeholder="Enter Your Email"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="email"
                        value={values.email}
                      />
                      {/* Validation — error-500 */}
                      {touched.email && errors.email && (
                        <small className="text-red-500 text-theme-xs mt-1 block text-left">
                          {errors.email}
                        </small>
                      )}
                    </div>

                    {/* ── Password Field ── */}
                    <div className="mb-4">
                      <label className="block font-semibold mb-1 text-left text-app-subtitle text-theme-sm">
                        Enter Password:
                      </label>
                      <div className="relative">
                        <input
                          className="
                            w-full px-4 py-2 rounded-lg outline-none
                            bg-white border border-gray-300
                            text-app-text placeholder-gray-400
                            focus:ring-2 focus:ring-app-accent focus:border-app-accent
                            transition-colors
                          "
                          name="pass"
                          placeholder="Enter Password"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type={showPassword ? 'text' : 'password'}
                          value={values.pass}
                        />
                        {/* Toggle icon — app-disable / app-text hover */}
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute right-3 top-3 text-app-disable hover:text-app-text focus:outline-none transition-colors"
                        >
                          {showPassword
                            ? <FontAwesomeIcon icon={faEyeSlash} />
                            : <FontAwesomeIcon icon={faEye} />
                          }
                        </button>
                      </div>
                      {touched.pass && errors.pass && (
                        <small className="text-red-500 text-theme-xs mt-1 block text-left">
                          {errors.pass}
                        </small>
                      )}
                    </div>

                    {/* ── Submit Error Banner — light red banner for light theme ── */}
                    {errors.submit && (
                      <div className="text-red-600 bg-red-50 border border-red-200 p-2 rounded-md mb-4 text-theme-sm">
                        {errors.submit}
                      </div>
                    )}

                    {/* ── Submit Button — app-accent: #EC2178 ── */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="
                        w-full font-semibold py-2 rounded-lg transition-opacity
                        text-white bg-app-accent
                        disabled:opacity-60 disabled:cursor-not-allowed
                        hover:opacity-90 active:opacity-80
                      "
                    >
                      {isSubmitting ? "Signing In..." : "Sign In"}
                    </button>

                    {/* ── Divider ── */}
                    <div className="flex items-center gap-3 my-4">
                      <hr className="flex-1 border-gray-200" />
                      <span className="text-app-disable text-theme-xs">or</span>
                      <hr className="flex-1 border-gray-200" />
                    </div>

                    {/* ── Forgot Password link — app-accent ── */}
                    <p className="text-center text-theme-sm">
                      <a
                        href="#"
                        className="text-app-accent hover:opacity-80 transition-colors font-medium"
                      >
                        Forgot password?
                      </a>
                    </p>

                    {loading && <FullPageLoader />}
                  </form>
                )}
              </Formik>
            </div>

          </div>
        </div>

        {/* Right Side - Image */}
        <div className="hidden md:flex md:w-1/2 items-center justify-center rounded-xl overflow-hidden">
          <img src={login} alt="Login" className="max-w-lg" />
        </div>

      </div>
    </div>
  );
};

export default SignIn;